import { useRef, useState, useCallback, useMemo } from "react";
import { Editor } from "@tinymce/tinymce-react";
import { Undo2, Redo2, Plus, ArrowLeft } from "lucide-react";
import BlockRenderer from "../../components/common/userform/BlockRenderer";
import { useGridBackground, useAutosave, useFormState } from "../../components/common/userform/hooks";
import {
  withConstraints,
  parseMaybeJSON,
  newRow,
  todayISO,
  sanitizeTemplateBody,
  compressImage,
} from "../../components/common/userform/utils";
import { DEFAULT_LAYOUT, DEFAULT_TABLE_COLUMNS } from "../../components/common/userform/constants";
import FormTemplateManager from "../../components/common/form/UserForm"; // Import from UserForm? No, FormTemplateManager is in components/common/FormTemplateManager?
// UserForm imported it from "./FormTemplateManager". Which is .../components/common/form/FormTemplateManager.jsx
import FormTemplateManagerComp from "../../components/common/form/FormTemplateManager";
import MobileFormContentManager from "../../components/common/mobile/MobileFormContentManager";
import { useNavigate } from "react-router-dom";

export default function MobileUserForm({
  initialLayout,
  mode = 2,
  module = "material",
  formType = "PO",
  scope = "project",
  project,
}) {
  const navigate = useNavigate();
  const editorRef = useRef(null);

  const [currentTemplate, setCurrentTemplate] = useState(null);

  // 상태 (리듀서 기반)
  const {
    state,
    setLayout,
    patchValues,
    patchBlocks,
    patchCustom,
    patchTables,
    undo,
    redo,
    toggleAlign,
    resetDirty,
  } = useFormState({
    layout: (initialLayout || DEFAULT_LAYOUT).map(withConstraints),
    blocks: {
      "block-title": { title: "제목", kind: "title", locked: false },
      "block-date": { title: "작성일", kind: "date", locked: false },
      "block-editor": { title: "본문", kind: "editor", locked: false },
      "block-photos": { title: "사진 첨부", kind: "photos", locked: false },
      "block-files": { title: "파일 첨부", kind: "files", locked: false },
      "block-note": { title: "메모", kind: "note", locked: false },
    },
    values: {
      title: "",
      dateStr: todayISO(),
      editorHtml: "<p>본문을 입력하세요.</p>",
      note: "",
      photos: [],
      files: [],
    },
  });

  const getBody = useCallback(
    () => ({
      layout: state.layout,
      values: state.values,
      blocksMeta: state.blocks,
      customValues: state.customValues,
      tables: state.tables,
      // 모바일 요약 정보용
      title: state.values.title,
      summary_component: null, // 필요시 추가
    }),
    [state]
  );

  useAutosave(getBody, [
    state.layout,
    state.values,
    state.blocks,
    state.customValues,
    state.tables,
  ]);

  // 템플릿 적용
  const resolveTemplateBody = async (tpl) => {
    let payload = tpl?.body_json ?? tpl?.template_snapshot ?? null;
    let body = parseMaybeJSON(payload) || payload;
    if (!body && tpl?.id) {
      try {
        const qs = project ? `?project=${encodeURIComponent(project)}` : "";
        const res = await fetch(`/api/doctemp/templates/${tpl.id}/${qs}`);
        if (res.ok) {
          const full = await res.json();
          body =
            parseMaybeJSON(full?.body_json) ||
            parseMaybeJSON(full?.template_snapshot);
        }
      } catch { }
    }
    return body;
  };

  const applyBody = useCallback(
    (raw) => {
      const body = sanitizeTemplateBody(raw);
      if (!body) return;
      setLayout((body.layout || []).map(withConstraints));
      patchValues({
        ...(body.values || {}),
        dateStr: body?.values?.dateStr || todayISO(),
      });
      if (body.blocksMeta) patchBlocks(body.blocksMeta);
      if (body.customValues) patchCustom(body.customValues);
      if (body.tables) patchTables(body.tables);
      resetDirty();
    },
    [setLayout, patchValues, patchBlocks, patchCustom, patchTables, resetDirty]
  );

  const handleTemplateSelect = async (tpl) => {
    setCurrentTemplate(tpl);
    const body = await resolveTemplateBody(tpl);
    if (body) applyBody(body);
  };

  // 핸들러들
  const handlePickPhotos = async (e) => {
    const list = Array.from(e.target.files || []);
    const next = [...state.values.photos];
    for (const file of list) {
      // 모바일은 압축 생략하거나 유틸 사용
      const src = await compressImage(file);
      next.push({
        id: crypto.randomUUID(),
        name: file.name,
        size: file.size,
        src,
      });
    }
    patchValues({ photos: next });
    e.target.value = "";
  };

  const insertImageToEditor = (img) => {
    const ed = editorRef.current;
    if (!ed) return;
    const figure = `<figure style="margin:0 0 8px 0;"><img src="${img.src}" alt="${img.name}" style="max-width:100%;height:auto" /><figcaption style="font-size:12px;color:#64748b">${img.name}</figcaption></figure>`;
    ed.insertContent(figure);
  };

  const removePhoto = (id) =>
    patchValues({ photos: state.values.photos.filter((p) => p.id !== id) });

  const handleAttachFiles = async (e) => {
    const list = Array.from(e.target.files || []);
    const next = [...state.values.files];
    for (const file of list) {
      const dataUrl = await new Promise((resolve) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.readAsDataURL(file);
      });
      next.push({
        id: crypto.randomUUID(),
        name: file.name,
        size: file.size,
        type: file.type || "application/octet-stream",
        dataUrl,
      });
    }
    patchValues({ files: next });
    e.target.value = "";
  };

  const removeFile = (id) =>
    patchValues({ files: state.values.files.filter((f) => f.id !== id) });

  const renameBlock = (id, title) =>
    patchBlocks({ [id]: { ...state.blocks[id], title } });
  const toggleLock = (id) =>
    patchBlocks({
      [id]: { ...state.blocks[id], locked: !state.blocks[id]?.locked },
    });

  // propsByKind 구성
  const propsByKind = {
    title: {
      value: state.values.title,
      onChange: (v) => patchValues({ title: v }),
      onRename: renameBlock,
      onToggleLock: toggleLock,
      onRemove: () => { },
    },
    date: {
      value: state.values.dateStr,
      onChange: (v) => patchValues({ dateStr: v }),
      onRename: renameBlock,
      onToggleLock: toggleLock,
      onRemove: () => { },
    },
    editor: {
      initialValue: state.values.editorHtml,
      onEditorChange: (v) => patchValues({ editorHtml: v }),
      onInit: (evt, editor) => (editorRef.current = editor),
      onInsertImage: insertImageToEditor,
      onRename: renameBlock,
      onToggleLock: toggleLock,
      onRemove: () => { },
    },
    custom: {
      values: state.customValues,
      onChange: (cid, val) =>
        patchCustom({ ...state.customValues, [cid]: val }),
      onRename: renameBlock,
      onToggleLock: toggleLock,
      onRemove: () => { },
    },
    photos: {
      inputProps: { onChange: handlePickPhotos },
      list: state.values.photos,
      onInsert: insertImageToEditor,
      onRemovePhoto: removePhoto,
      onRename: renameBlock,
      onToggleLock: toggleLock,
      onRemove: () => { },
    },
    note: {
      value: state.values.note,
      onChange: (v) => patchValues({ note: v }),
      onRename: renameBlock,
      onToggleLock: toggleLock,
      onRemove: () => { },
    },
    files: {
      files: state.values.files,
      onAttach: handleAttachFiles,
      onRemoveFile: removeFile,
      onRename: renameBlock,
      onToggleLock: toggleLock,
      onRemove: () => { },
    },
    table: {
      onAddRow: (bid) =>
        patchTables({
          [bid]: {
            ...(state.tables[bid] || {
              columns: DEFAULT_TABLE_COLUMNS,
              rows: [],
            }),
            rows: [...(state.tables[bid]?.rows || []), newRow()],
          },
        }),
      onRemoveRow: (bid, rid) => {
        const t = state.tables[bid];
        if (!t) return;
        patchTables({
          [bid]: { ...t, rows: t.rows.filter((r) => r._id !== rid) },
        });
      },
      onUpdateCell: (bid, rid, key, value) => {
        const t = state.tables[bid];
        if (!t) return;
        const cols = t.columns || DEFAULT_TABLE_COLUMNS;
        const rows = t.rows.map((r) =>
          r._id !== rid
            ? r
            : {
              ...r,
              [key]:
                cols.find((c) => c.key === key)?.type === "number"
                  ? Number(value)
                  : value,
            }
        );
        patchTables({ [bid]: { ...t, rows } });
      },
      onRename: renameBlock,
      onToggleLock: toggleLock,
      onRemove: () => { },
    },
  };

  // Layout Sorting: Y first, then X
  const sortedLayout = [...state.layout].sort((a, b) => a.y - b.y || a.x - b.x);

  return (
    <MobileFormContentManager
      params={{ module, formType, mode, scope, project }}
      getBody={getBody}
      docName={state.values.title || "문서 작성"}
      redirectTo={`/project/${project}/materials/materialboard`}
      docDescribe="템플릿 내용을 입력하세요."
    >
      <div className="space-y-4">
        {/* 템플릿 선택기 */}
        <div className="bg-white p-3 rounded-lg border">
          <FormTemplateManagerComp
            params={{ module, formType, mode, scope, project }}
            getBody={getBody}
            onApply={(body) => applyBody(body)}
            onChangeTemplate={handleTemplateSelect}
            autoApplyOnMount={false}
          // Mobile prop? FormTemplateManagerComp might render desktop UI.
          // We assume it renders a Select dropdown or similar.
          // If it renders a full manager, might be issue. 
          // But keeping it for now.
          />
        </div>

        {/* 블록 렌더링 (Linear) */}
        {sortedLayout.map((item) => {
          const meta = state.blocks[item.i];
          if (!meta) return null;
          return (
            <div key={item.i} className="bg-white p-3 rounded-lg border shadow-sm">
              <div className="mb-2 font-medium text-sm text-gray-700 border-b pb-1">
                {meta.title}
              </div>
              <BlockRenderer
                kind={meta.kind}
                meta={meta}
                alignMode={false} // 모바일에서는 정렬 모드 끔
                bid={item.i}
                t={state.tables[item.i]}
                {...propsByKind[meta.kind]}
              />
            </div>
          );
        })}
      </div>
    </MobileFormContentManager>
  );
}
